import { Table } from "containers/Table";
import { IColum } from "containers/Table/interfaces";
import {
  AcoesContainer,
  AlertaAReceber,
  AlertaHistorico,
  BarraFiltroRapido,
  Button,
  ButtonIcon,
  Container,
  DivAcoes,
  ExportItemSubtitulo,
  ExportItemTitulo,
  ExportMenu,
  ExportMenuItem,
  ExportPopover,
  FlagSemArquivo,
  FlagUrgente,
  Header,
  PagamentoUrgenteContainer,
  CustosCelula,
  StatusBadge,
  TagCustoExtra,
  TextoHeader,
} from "./style";
import { formatarDinheiro } from "_utils/Money";
import { converterDataHoraBr, converterDataBr } from "_utils/Data";
import { toastErro } from "components/Toast";
import { IListarLoteGerenciarPagamento } from "../../../../../interface/IListarLoteGerenciarPagamento";
import { IRelatorioDetalhadoPagamento } from "../../../../../interface/IRelatorioDetalhadoPagamento";
import {
  FaCheck,
  FaColumns,
  FaDollarSign,
  FaDownload,
  FaExclamationTriangle,
  FaEye,
  FaFileExcel,
  FaFlag,
  FaHistory,
  FaImage,
  FaMoneyCheckAlt,
  FaPen,
  FaUniversity,
} from "react-icons/fa";
import { SwitchCustom } from "components/Switch";
import { Overlay, Popover, Tooltip } from "react-bootstrap";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import { utils, writeFileXLSX } from "xlsx";
import { useRef, useState } from "react";
import { ModalConfigColunas } from "../ModalConfigColunas";
import { defaultConfigColunas } from "../../constants/defaultConfigColunas";

const STORAGE_KEY = "config_listar_aprovados_pagamentos";

const STATUS_LABEL: Record<string, string> = {
  PENDENTE: "Pendente",
  VALIDADO: "Validado",
  PAGO: "Pago",
};

interface IListarProps {
  dados: IListarLoteGerenciarPagamento[];
  handleLimitChange: (e: number) => void;
  handlePageChange: (e: number) => void;
  totalRows: number;
  setRowsSelected: (rows: IListarLoteGerenciarPagamento[]) => void;
  selectedRows: IListarLoteGerenciarPagamento[];
  linhasPorPagina: number;
  clearRows: boolean;
  pagamentoUrgenteAtivo: boolean;
  onTogglePagamentoUrgente: (urgente: boolean) => void;
  onClickSolicitarCorrecao: () => void;
  onClickValidar: () => void;
  onClickConfirmarPagamento: () => void;
  onClickVisualizar: (idLote: number) => void;
  onClickDadosPagamento: (idLote: number) => void;
  onClickComprovante: (idLote: number) => void;
  onClickHistorico: (idLote: number) => void;
  onClickAnexarRecibo: (idLote: number) => void;
  onBaixarRecibo: (idLote: number, numeroNfRecibo: string) => void;
  onOrdenar: (campo: string, direcao: string) => void;
  relatorioDetalhado: () => Promise<IRelatorioDetalhadoPagamento[] | null>;
  podeSolicitarCorrecao: boolean;
  podeValidar: boolean;
  podeConfirmarPagamento: boolean;
}

function CustosColunaCell({ custos }: { custos: string[] }) {
  const MAX_VISIVEIS = 1;
  const visiveis = custos.slice(0, MAX_VISIVEIS);
  const extras = custos.length - MAX_VISIVEIS;

  const popover = (
    <Popover id="popover-custos" style={{ minWidth: 220 }}>
      <Popover.Body style={{ padding: "16px" }}>
        <div
          style={{
            border: "1px solid #dee2e6",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        >
          {custos.map((s, i) => (
            <div
              key={i}
              style={{
                padding: "8px 14px",
                fontSize: "0.85rem",
                borderBottom:
                  i < custos.length - 1 ? "1px solid #f0f0f0" : "none",
                backgroundColor: "#fff",
              }}
            >
              {s}
            </div>
          ))}
        </div>
      </Popover.Body>
    </Popover>
  );

  return (
    <CustosCelula>
      {visiveis.map((s, i) => (
        <span
          key={i}
          style={{
            fontSize: "0.85rem",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: "0 1 auto",
            minWidth: 0,
          }}
        >
          {s}
        </span>
      ))}
      {extras > 0 && (
        <OverlayTrigger
          placement="top"
          trigger={["hover", "focus"]}
          overlay={popover}
        >
          <TagCustoExtra>+{extras}</TagCustoExtra>
        </OverlayTrigger>
      )}
    </CustosCelula>
  );
}

export function ListarAprovados({
  dados,
  handleLimitChange,
  handlePageChange,
  totalRows,
  setRowsSelected,
  selectedRows,
  linhasPorPagina,
  clearRows,
  pagamentoUrgenteAtivo,
  onTogglePagamentoUrgente,
  onClickSolicitarCorrecao,
  onClickValidar,
  onClickConfirmarPagamento,
  onClickVisualizar,
  onClickDadosPagamento,
  onClickComprovante,
  onClickHistorico,
  onClickAnexarRecibo,
  onBaixarRecibo,
  onOrdenar,
  relatorioDetalhado,
  podeSolicitarCorrecao,
  podeValidar,
  podeConfirmarPagamento,
}: IListarProps) {
  const [showModalColunas, setShowModalColunas] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportRef = useRef<HTMLButtonElement>(null);
  const [configColunas, setConfigColunas] = useState<
    { nome: string; exibir: boolean }[]
  >(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : defaultConfigColunas;
    } catch {
      return defaultConfigColunas;
    }
  });

  function handleSaveConfigColunas(
    novaConfig: { nome: string; exibir: boolean }[],
  ) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(novaConfig));
    setConfigColunas(novaConfig);
  }

  const omit = (nome: string) =>
    configColunas.some((c) => c.nome === nome && !c.exibir);

  const columns: IColum<IListarLoteGerenciarPagamento>[] = [
    {
      name: "Lote",
      selector: ({ id }: IListarLoteGerenciarPagamento) => id,
      width: "90px",
      omit: omit("idLote"),
      sortable: true,
      sortField: "id",
    },
    {
      name: "Observação",
      selector: ({ observacao }: IListarLoteGerenciarPagamento) =>
        observacao?.trim() ? observacao : "-",
      width: "180px",
      omit: omit("observacao"),
      sortable: true,
      sortField: "observacao",
    },
    {
      name: "Usuário",
      selector: ({ nomeUsuario }: IListarLoteGerenciarPagamento) =>
        nomeUsuario ?? "-",
      width: "150px",
      omit: omit("usuario"),
      sortable: true,
      sortField: "nomeUsuario",
    },
    {
      name: "Aprovação",
      selector: ({ dataHoraAprovacao }: IListarLoteGerenciarPagamento) =>
        dataHoraAprovacao ? converterDataHoraBr(dataHoraAprovacao) : "-",
      width: "170px",
      omit: omit("aprovacao"),
      sortable: true,
      sortField: "dataHoraAprovacao",
    },
    {
      name: "Recibo / NF",
      selector: ({
        numeroNfRecibo,
        pagamentoUrgente,
        semArquivoAnexado,
        id,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
          {!semArquivoAnexado ? (
            <span
              className="link-primary"
              style={{ cursor: "pointer" }}
              onClick={() => onBaixarRecibo(id, numeroNfRecibo ?? "")}
            >
              {numeroNfRecibo ?? "-"}
            </span>
          ) : (
            <span>{numeroNfRecibo ?? "-"}</span>
          )}
          {pagamentoUrgente && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaFlag style={{ marginRight: 4, color: "#ee5353" }} />
                  Pagamento urgente
                </Tooltip>
              }
            >
              <FlagUrgente>
                <FaFlag />
              </FlagUrgente>
            </OverlayTrigger>
          )}
          {semArquivoAnexado && (
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Anexar arquivo de Recibo / NF</Tooltip>}
            >
              <FlagSemArquivo onClick={() => onClickAnexarRecibo(id)}>
                <FaImage />
              </FlagSemArquivo>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "170px",
      omit: omit("reciboNf"),
      sortable: true,
      sortField: "numeroNfRecibo",
    },
    {
      name: "Beneficiário",
      selector: ({ nomeBeneficiario }: IListarLoteGerenciarPagamento) =>
        nomeBeneficiario ?? "-",
      width: "150px",
      omit: omit("beneficiario"),
      sortable: true,
      sortField: "nomeBeneficiario",
    },
    {
      name: "Processos",
      selector: ({ qtdProcessos }: IListarLoteGerenciarPagamento) =>
        qtdProcessos ?? 0,
      width: "110px",
      omit: omit("qtdProcessos"),
      sortable: true,
      sortField: "qtdProcessos",
    },
    {
      name: "Custos",
      selector: ({ custosDescricao }: IListarLoteGerenciarPagamento) => (
        <CustosColunaCell custos={custosDescricao ?? []} />
      ),
      omit: omit("custosDescricao"),
      sortable: true,
      sortField: "custosDescricao",
    },
    {
      name: <span>Total a pagar</span>,
      selector: ({ totalAPagar }: IListarLoteGerenciarPagamento) =>
        totalAPagar != null ? formatarDinheiro(Number(totalAPagar)) : "-",
      width: "130px",
      omit: omit("totalAPagar"),
      sortable: true,
      sortField: "totalAPagar",
    },
    {
      name: (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Valor dos custos do processo</Tooltip>}
        >
          <span>Valor custos</span>
        </OverlayTrigger>
      ),
      selector: ({
        custos,
        alertaCustoTotalAPagar,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{custos != null ? formatarDinheiro(Number(custos)) : "-"}</span>
          {alertaCustoTotalAPagar && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaExclamationTriangle
                    style={{ marginRight: 4, color: "#fe8f14" }}
                  />
                  Custo do processo superior ao total a pagar.
                </Tooltip>
              }
            >
              <AlertaAReceber>
                <FaExclamationTriangle />
              </AlertaAReceber>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "120px",
      omit: omit("valorCustos"),
      sortable: true,
      sortField: "custos",
    },
    {
      name: (
        <OverlayTrigger
          placement="top"
          overlay={<Tooltip>Serviços a receber</Tooltip>}
        >
          <span>A receber</span>
        </OverlayTrigger>
      ),
      selector: ({
        aReceber,
        alertaAReceber,
      }: IListarLoteGerenciarPagamento) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>{formatarDinheiro(Number(aReceber ?? 0))}</span>
          {alertaAReceber && (
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip>
                  <FaExclamationTriangle
                    style={{ marginRight: 4, color: "#fe8f14" }}
                  />
                  Valor do serviço a receber inferior ao custo do processo.
                </Tooltip>
              }
            >
              <AlertaAReceber>
                <FaExclamationTriangle />
              </AlertaAReceber>
            </OverlayTrigger>
          )}
        </div>
      ),
      width: "130px",
      omit: omit("aReceber"),
      sortable: true,
      sortField: "aReceber",
    },
    {
      name: "Status",
      selector: ({ statusPagamento }: IListarLoteGerenciarPagamento) => (
        <StatusBadge tipo={statusPagamento}>
          {STATUS_LABEL[statusPagamento] ?? statusPagamento}
        </StatusBadge>
      ),
      width: "120px",
      omit: omit("status"),
      sortable: true,
      sortField: "statusPagamento",
    },
    {
      name: <span>Data Pagamento</span>,
      selector: ({ dataHoraPagamento }: IListarLoteGerenciarPagamento) =>
        dataHoraPagamento ? converterDataBr(dataHoraPagamento) : "-",
      width: "110px",
      omit: omit("dataPagamento"),
      sortable: true,
      sortField: "dataHoraPagamento",
    },
    {
      name: "Ações",
      selector: ({
        id,
        alertaHistorico,
        statusPagamento,
      }: IListarLoteGerenciarPagamento) => {
        const comprovanteHabilitado = statusPagamento === "PAGO";
        return (
        <AcoesContainer>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Visualizar lote</Tooltip>}
          >
            <span onClick={() => onClickVisualizar(id)}>
              <FaEye />
            </span>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Dados para pagamento</Tooltip>}
          >
            <span onClick={() => onClickDadosPagamento(id)}>
              <FaUniversity />
            </span>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                {comprovanteHabilitado
                  ? "Comprovante de pagamento"
                  : "Comprovante disponível apenas após o pagamento"}
              </Tooltip>
            }
          >
            <span
              onClick={
                comprovanteHabilitado ? () => onClickComprovante(id) : undefined
              }
              style={
                comprovanteHabilitado
                  ? undefined
                  : { cursor: "not-allowed", color: "#c0c0c0" }
              }
            >
              <FaMoneyCheckAlt />
            </span>
          </OverlayTrigger>
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip>Histórico de atualizações</Tooltip>}
          >
            <span onClick={() => onClickHistorico(id)}>
              <FaHistory />
              {alertaHistorico && <AlertaHistorico />}
            </span>
          </OverlayTrigger>
        </AcoesContainer>
        );
      },
      width: "130px",
    },
  ];

  const exportarDetalhado = async () => {
    const linhas = await relatorioDetalhado();
    if (!linhas) return;
    if (!linhas.length) {
      toastErro("Nenhum dado encontrado para o relatório detalhado.");
      return;
    }

    const dadosPlanilha = linhas.map((item) => ({
      "ID LOTE": item?.idLote ?? "-",
      "ID PROCESSO": item?.idProcesso ?? "-",
      PLACA: item?.placa ?? "-",
      RENAVAM: item?.renavam ?? "-",
      CUSTOS: item?.custo ?? "-",
      EMPRESA: item?.nomeEmpresa ?? "-",
      "CNPJ/CPF BENEFICIÁRIO": item?.cpfCnpjFornecedor ?? "-",
      "NOME BENEFICIÁRIO": item?.nomeFornecedor ?? "-",
      "VALOR DO PAGAMENTO": item?.valorPagamento ? Number(item.valorPagamento) : 0,
      "CHAVE PIX": item?.chavePix ?? "-",
      "TITULAR DA CONTA PIX": item?.titularPix ?? "-",
      BANCO: item?.banco ?? "-",
      AGÊNCIA: item?.agencia ?? "-",
      "CONTA CORRENTE": item?.contaCorrente ?? "-",
      "CNPJ/CPF TITULAR CONTA CORRENTE": item?.cpfCnpjTitularContaCorrente ?? "-",
      "TITULAR CONTA CORRENTE": item?.titularContaCorrente ?? "-",
      "STATUS PAGAMENTO": STATUS_LABEL[item?.statusPagamento] ?? item?.statusPagamento ?? "-",
      "DATA/HORA INCLUSÃO": item?.dataInclusao ? converterDataHoraBr(item.dataInclusao) : "-",
      "DATA/HORA APROVAÇÃO": item?.dataHoraAprovacao ? converterDataHoraBr(item.dataHoraAprovacao) : "-",
      "RESPONSÁVEL APROVAÇÃO": item?.responsavelAprovacao ?? "-",
      "DATA/HORA VALIDAÇÃO": item?.dataHoraValidacao ? converterDataHoraBr(item.dataHoraValidacao) : "-",
      "RESPONSÁVEL VALIDAÇÃO": item?.responsavelValidacao ?? "-",
      "DATA PAGAMENTO": item?.dataHoraPagamento ? converterDataBr(item.dataHoraPagamento) : "-",
      "RESPONSÁVEL PAGAMENTO": item?.responsavelPagamento ?? "-",
    }));

    const planilha = utils.json_to_sheet(dadosPlanilha);

    const colunaValor = 8;
    const range = utils.decode_range(planilha["!ref"] ?? "A1");
    for (let row = range.s.r + 1; row <= range.e.r; row++) {
      const cellRef = utils.encode_cell({ r: row, c: colunaValor });
      if (planilha[cellRef]) {
        planilha[cellRef].z = '"R$ "#,##0.00';
      }
    }

    planilha["!cols"] = Array(24).fill({ wch: 22 });
    const workBook = utils.book_new();
    utils.book_append_sheet(workBook, planilha, "Detalhes");

    const hoje = new Date();
    const dia = String(hoje.getDate()).padStart(2, "0");
    const mes = String(hoje.getMonth() + 1).padStart(2, "0");
    const ano = hoje.getFullYear();
    writeFileXLSX(workBook, `Pagamento_detalhado_${dia}-${mes}-${ano}.xlsx`);
  };

  const baixarComprovantesSelecionados = () => {
    if (!selectedRows.length) {
      toastErro("Selecione ao menos um lote para baixar os comprovantes.");
      return;
    }
    selectedRows.forEach((row) => onClickComprovante(row.id));
  };

  return (
    <Container>
      <Header>
        <TextoHeader>Lotes aprovados para pagamento</TextoHeader>
        <DivAcoes>
          <ButtonIcon
            ref={exportRef}
            outline
            variante="primary"
            onClick={() => setShowExportMenu((v) => !v)}
            title="Exportar"
          >
            <FaDownload />
          </ButtonIcon>
          <Overlay
            target={exportRef.current}
            show={showExportMenu}
            placement="bottom-end"
            rootClose
            onHide={() => setShowExportMenu(false)}
          >
            <ExportPopover id="popover-exportar-aprovados">
              <Popover.Body style={{ padding: 0 }}>
                <ExportMenu>
                  <ExportMenuItem
                    onClick={() => {
                      setShowExportMenu(false);
                      baixarComprovantesSelecionados();
                    }}
                  >
                    <FaMoneyCheckAlt />
                    <div>
                      <ExportItemTitulo>Baixar comprovantes</ExportItemTitulo>
                      <ExportItemSubtitulo>
                        Selecione os processos que deseja
                      </ExportItemSubtitulo>
                    </div>
                  </ExportMenuItem>
                  <ExportMenuItem
                    onClick={() => {
                      setShowExportMenu(false);
                      exportarDetalhado();
                    }}
                  >
                    <FaFileExcel />
                    <div>
                      <ExportItemTitulo>Relatório detalhado</ExportItemTitulo>
                    </div>
                  </ExportMenuItem>
                </ExportMenu>
              </Popover.Body>
            </ExportPopover>
          </Overlay>
          <Button
            outline
            variante="primary"
            onClick={() => setShowModalColunas(true)}
            title="Configurar colunas"
            style={{ width: "40px", padding: 0 }}
          >
            <FaColumns />
          </Button>
          {podeSolicitarCorrecao && (
            <Button
              outline
              variante="primary"
              disabled={
                !selectedRows.length ||
                selectedRows.some((row) => row.statusPagamento !== "PENDENTE")
              }
              onClick={onClickSolicitarCorrecao}
              style={{ width: "150px" }}
            >
              <FaPen /> Solicitar correção
            </Button>
          )}
          {podeValidar && (
            <Button
              variante="primary"
              disabled={
                !selectedRows.length ||
                selectedRows.some((row) => row.statusPagamento !== "PENDENTE")
              }
              onClick={onClickValidar}
              style={{ width: "120px" }}
            >
              <FaCheck />
              Validar lote
            </Button>
          )}
          {podeConfirmarPagamento && (
            <Button
              disabled={
                !selectedRows.length ||
                selectedRows.some((row) => row.statusPagamento === "PAGO")
              }
              onClick={onClickConfirmarPagamento}
              style={{ width: "170px" }}
            >
              <FaDollarSign /> Confirmar pagamento
            </Button>
          )}
        </DivAcoes>
      </Header>
      <hr style={{ marginTop: "1.5rem" }} />
      <BarraFiltroRapido>
        <PagamentoUrgenteContainer>
          <SwitchCustom
            onChange={(e) => onTogglePagamentoUrgente(e.target.checked)}
            checked={pagamentoUrgenteAtivo}
            title="Filtrar apenas pagamentos urgentes"
          />
          Pagamento urgente
        </PagamentoUrgenteContainer>
      </BarraFiltroRapido>
      <Table
        keyField="id"
        totalRows={totalRows}
        pagination={true}
        columns={columns}
        data={dados}
        paginationRowsPerPageOptions={[30, 50, 100, 300]}
        loading={false}
        onChangeRowsPerPage={handleLimitChange}
        onChangePage={handlePageChange}
        noSubHeader
        possuiTooltip={false}
        selectableRows
        setRowsSelected={setRowsSelected}
        rowsPerPage={linhasPorPagina}
        headerFixo
        clearSelectedRows={clearRows}
        handleSortFunction={(coluna, direcao) =>
          onOrdenar((coluna.sortField as string) ?? "", direcao)
        }
      />
      <ModalConfigColunas
        show={showModalColunas}
        handleClose={() => setShowModalColunas(false)}
        configColunas={configColunas}
        onSave={handleSaveConfigColunas}
      />
    </Container>
  );
}
